import React, {useContext, useEffect} from 'react';
import Joi from 'joi';
import {Text} from 'components/forms/fields';
import Section from 'components/forms/Section';
import {Typography} from '@material-ui/core';
import fields from 'components/forms/ssl/fields';
import {useFormPersistence} from 'components/forms/hooks';
import {AppCtx} from 'context/AppContext';

const watertypes = [
    'total_days_oceans',
    'total_days_near_coastal',
    'total_days_inland',
    'total_days_great_lakes',
    'total_days_western_rivers',
    'total_days_polar',
];

const sumArray = (total, num) => {
    const parsed = parseInt(num);
    if (!isNaN(parsed)) {
        return total + parsed;
    }
    return total;
};

const schema = {
    number_days_on_letter: Joi.number()
        .integer()
        .required()
        .min(1)
        .custom((value, helpers) => {
            const sum = watertypes
                .map(type => helpers.state.ancestors[0][type])
                .reduce(sumArray, 0);

            if (
                sum !==
                parseInt(helpers.state.ancestors[0].number_days_on_letter)
            ) {
                return helpers.error('any.invalid', {sum});
            }

            return value;
        })
        .messages({
            'any.invalid':
                'Total number of sea days must equal the sum of days on all water types. Current sum of all water types is {#sum}',
        }),
    ...Object.fromEntries(
        watertypes.map(type => [
            type,
            Joi.number()
                .integer()
                .min(0)
                .max(
                    Joi.ref('number_days_on_letter', {
                        adjust: value => parseInt(value),
                    })
                )
                .allow('', null)
                .messages({
                    'number.base': 'This field requires a number value',
                    'number.max':
                        'Days must be less than or equal to the total number of days on the letter',
                }),
        ])
    ),
};

const LetterSummary = props => {
    const {state} = useFormPersistence();
    const {tankermanId, setTankermanId} = useContext(AppCtx);

    const initialValues = {
        number_days_on_letter: state?.vessel_entries?.reduce(
            (totalDays, vessel) =>
                totalDays +
                (parseInt(
                    vessel?.service_entries?.reduce(
                        (days, service) =>
                            days + (parseInt(service?.total_number_days) || 0),
                        0
                    )
                ) || 0),
            0
        ),
        ...Object.fromEntries(
            watertypes.map(type => {
                const waterType = type.replace(/total_days_/, '');
                return [
                    type,
                    state?.vessel_entries?.reduce(
                        (totalDays, vessel) =>
                            totalDays +
                            (parseInt(
                                vessel?.service_entries?.reduce(
                                    (days, service) => {
                                        if (
                                            service?.watertype
                                                ?.trim()
                                                ?.toLowerCase()
                                                ?.replaceAll(/\s/g, '_') ===
                                            waterType
                                        ) {
                                            days =
                                                days +
                                                (parseInt(
                                                    service?.total_number_days
                                                ) || 0);
                                        }

                                        return days;
                                    },
                                    0
                                )
                            ) || 0),
                        0
                    ),
                ];
            })
        ),
    };

    useEffect(() => {
        if (tankermanId) {
            setTankermanId(null);
        }
        //eslint-disable-next-line
    }, [tankermanId]);

    return (
        <Section
            {...props}
            {...{schema, initialValues}}
            validateOnChange={[...watertypes, 'number_days_on_letter']}
        >
            <Typography variant='h6' align='left'>
                Letter Summary
            </Typography>
            <Text
                name={fields.number_days_on_letter.name}
                label={fields.number_days_on_letter.label}
                inputProps={{type: 'tel'}}
                required
            />
            <Typography variant='body1'>
                What is the total number of days you served on each route,
                recorded on this letter?
            </Typography>
            {watertypes.map(route => (
                <Text
                    key={route}
                    name={fields[route].name}
                    label={fields[route].label}
                    inputProps={{type: 'tel'}}
                />
            ))}
            {props.children}
        </Section>
    );
};

export default LetterSummary;
